// Verify OTP for Pay Now – same UI as apply/otp-mobile
// Calls POST /user/verify-otp-for-paynow with { PAN, otp, phone_number }
import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { squidloansUserAPI } from "../services/api";
import { useAuth } from "../Context/AuthContext";
import { leadStageToRouteMap } from "../Utils/constants";
import FlowFooter from "../components/FlowFooter";
import Toast from "../components/Toast";

function VerifyRepayOtp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const stateData = location.state || {};
  const { PAN, phone_number, message: toastMessage } = stateData;

  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(60);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "warning" });
  const toastShownRef = useRef(false);

  const showToast = (message, type = "warning") => {
    setToast({ show: true, message, type });
  };
  const hideToast = () => {
    setToast({ show: false, message: "", type: "warning" });
  };

  useEffect(() => {
    if (!PAN || !phone_number) {
      navigate("/repayloan", { replace: true });
    }
  }, [PAN, phone_number, navigate]);

  useEffect(() => {
    if (toastMessage && !toastShownRef.current) {
      toastShownRef.current = true;
      setToast({ show: true, message: toastMessage, type: "success" });
    }
  }, [toastMessage]);

  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setInterval(() => setResendCooldown((prev) => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    try {
      setIsLoading(true);
      setOtp("");
      setError("");
      await squidloansUserAPI.sendOtpForPaynow(PAN);
      setResendCooldown(60);
      showToast("OTP resent successfully", "success");
    } catch (err) {
      setError(err?.message || "Failed to resend OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && value.length <= 6) {
      setOtp(value);
      setError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!otp) {
      setError("Please enter the OTP");
      return;
    }
    if (otp.length < 4) {
      setError("OTP must be at least 4 digits");
      return;
    }
    if (!phone_number) {
      setError("Phone number not found. Please go back and try again.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await squidloansUserAPI.verifyOtpForPaynow({
        PAN,
        otp,
        phone_number,
      });
      if (response?.success === true && response?.token) {
        login(response.token);
        localStorage.setItem("userPhone", phone_number);
        const journey = await squidloansUserAPI.getJourney();
        const stage = journey?.journey?.lead_stage;
        if (stage && leadStageToRouteMap[stage]) {
          navigate(leadStageToRouteMap[stage]);
        } else {
          navigate("/apply-now");
        }
      } else if (response?.success === true) {
        showToast(response?.message || "Verification successful", "success");
        navigate("/thanku", { replace: true });
      } else {
        setError(response?.message || "Verification failed");
      }
    } catch (err) {
      setError(err?.message || "Invalid OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!PAN || !phone_number) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#FFFFFF] to-[#F1F5F9] text-[#1E293B] relative flex flex-col">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="hidden sm:block absolute -top-20 -right-10 w-80 h-80 bg-[#FF6B35]/10 blur-[120px]"></div>
        <div className="hidden sm:block absolute top-32 -left-10 w-72 h-72 bg-[#1E3A8A]/10 blur-[110px]"></div>
        <div className="hidden sm:block absolute bottom-0 right-1/3 w-96 h-96 bg-[#FF6B35]/5 blur-[130px]"></div>
      </div>

      <div className="relative flex-1 flex flex-col pt-20 sm:pt-24 md:pt-28 pb-6 sm:pb-10 px-3 sm:px-4 md:px-6 lg:px-10 mt-0 overflow-y-auto">
        <div className="max-w-md w-full mx-auto space-y-4 sm:space-y-6 py-2 sm:py-4">
          <div className="text-center space-y-2 sm:space-y-3 mb-2 sm:mb-4 px-2">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold leading-tight text-[#1E3A8A]">
              Verify your <span className="text-[#FF6B35]">repayment</span>
            </h1>
            <p className="text-sm sm:text-base text-[#64748B]">
              OTP sent to your phone number
            </p>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 shadow-2xl shadow-[#FF6B35]/10">
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div>
                <label
                  htmlFor="otp"
                  className="text-xs sm:text-sm font-semibold tracking-wide text-[#1E293B] mb-1 block"
                >
                  Enter OTP <span className="text-[#FF6B35]">*</span>
                </label>
                <input
                  type="text"
                  id="otp"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={otp}
                  onChange={handleOtpChange}
                  placeholder="Enter 6 digit OTP"
                  maxLength={6}
                  className={`w-full px-3 sm:px-4 py-2.5 sm:py-3.5 rounded-xl sm:rounded-2xl bg-white border text-sm sm:text-base text-center font-medium ${
                    error ? "border-red-500" : "border-[#E2E8F0] focus:border-[#FF6B35]"
                  } text-[#1E293B] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/40 transition-all`}
                />
                {error && (
                  <p className="text-xs text-red-500 mt-1 sm:mt-2">{error}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 sm:py-3.5 rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white text-sm sm:text-base font-semibold hover:opacity-90 transition-all disabled:opacity-70 disabled:cursor-not-allowed shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  "Submit"
                )}
              </button>

              <div className="text-center">
                <p className="text-xs sm:text-sm text-[#64748B] mb-2">
                  Didn&apos;t receive the code?
                </p>
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={isLoading || resendCooldown > 0}
                  className="px-4 sm:px-6 py-2 sm:py-2.5 rounded-lg sm:rounded-xl bg-gradient-to-r from-[#FF6B35] to-[#ff8c42] text-white text-xs sm:text-sm font-semibold hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                >
                  {resendCooldown > 0 ? `Resend OTP (${resendCooldown}s)` : "Resend OTP"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {toast.show && (
        <Toast message={toast.message} type={toast.type} onClose={hideToast} />
      )}
      <FlowFooter />
    </div>
  );
}

export default VerifyRepayOtp;
